import React, { useState } from "react";
import { toast } from "sonner";
import { ClipboardPaste, Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";

export const MapLinkPaste = ({ onPick, endpoint = "/admin/maps/resolve" }: { onPick: (lat: number, lng: number) => void; endpoint?: string }) => {
  const { t } = usePrefs();
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const resolve = async (text: string) => {
    if (text.trim().length < 3) return;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, { text: text.trim() });
      onPick(data.lat, data.lng);
      toast.success(t("ml.done"));
    } catch (e) { toast.error(errorText(e, t)); } finally { setBusy(false); }
  };
  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setV(text);
      resolve(text);
    } catch { toast.error(t("ml.noClip")); }
  };
  return (
    <div className="space-y-1.5" data-testid="map-link-paste">
      <p className="text-xs font-bold">{t("ml.label")}</p>
      <div className="flex gap-2">
        <Input dir="ltr" value={v} placeholder={t("ml.placeholder")} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setV(e.target.value)}
          onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => { const x = e.clipboardData.getData("text"); if (x) { e.preventDefault(); setV(x); resolve(x); } }}
          onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter") { e.preventDefault(); resolve(v); } }}
          data-testid="map-link-input" className="h-11 rounded-2xl flex-1 min-w-0 text-sm" />
        {v.trim()
          ? <button type="button" onClick={() => resolve(v)} disabled={busy} data-testid="map-link-go" className="h-11 px-4 rounded-2xl bg-primary text-primary-foreground text-sm font-bold inline-flex items-center gap-1.5 shrink-0 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}{t("ml.go")}</button>
          : <button type="button" onClick={paste} data-testid="map-link-paste-button" className="h-11 px-4 rounded-2xl border bg-background text-sm font-bold inline-flex items-center gap-1.5 shrink-0"><ClipboardPaste className="h-4 w-4" />{t("ml.paste")}</button>}
      </div>
      <p className="text-[11px] text-muted-foreground">{t("ml.hint")}</p>
    </div>
  );
};
